import LecturerLayout from '../../components/LecturerLayout';
import PageLoader from '../../components/PageLoader';
import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const formatDate = (value) => {
    if (!value) return '';
    const normalized = String(value).includes('T') ? value : String(value).replace(' ', 'T');
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString();
};

const formatTopupMarks = (value) => {
    if (value === null || value === undefined) return '-';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
};

export default function Correction() {

    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const [filters, setFilters] = useState({ courses: [], batches: [], subjects: [] });
    const [stats, setStats] = useState({ total: 0, regular: 0, topup: 0 });
    const [loading, setLoading] = useState(true);
    
    // Filter states
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showGradingModal, setShowGradingModal] = useState(false);
    const [viewMode, setViewMode] = useState('pending');

    const isPendingView = viewMode === 'pending';
    const statLabels = isPendingView
        ? {
            total: 'Total Pending',
            regular: 'Pending Regular Assignments',
            topup: 'Pending Top-Up Assignments',
        }
        : {
            total: 'Total Corrected',
            regular: 'Corrected Regular Assignments',
            topup: 'Corrected Top-Up Assignments',
        };
    const listTitle = isPendingView ? 'Pending Submissions' : 'Corrected Submissions';
    const listSubtitle = isPendingView ? 'Ready for your review' : 'Previously graded records';
    const statusCountLabel = isPendingView ? 'pending' : 'corrected';
    const getMarksBadgeCopy = (submission) => {
        if (submission?.marks === null || submission?.marks === undefined) return null;
        if (submission.submission_type === 'regular') {
            return `Grade: ${submission.marks}`;
        }

        const numericMarks = Number(submission.marks);
        const hasSecondMarks = submission.second_marks !== null && submission.second_marks !== undefined;
        const shouldShowSecondMarks = hasSecondMarks && Number.isFinite(numericMarks) && numericMarks < 40;

        if (shouldShowSecondMarks) {
            return `First Marks: ${formatTopupMarks(submission.marks)} • Second Marks: ${formatTopupMarks(submission.second_marks)}`;
        }

        return `Marks: ${formatTopupMarks(submission.marks)}`;
    };
    const renderSubmissionCard = (submission, keyPrefix) => {
        const studentImageUrl = submission.studentImage;
        const marksBadge = getMarksBadgeCopy(submission);
        const submissionDate = formatDate(submission.submissionDate) || '—';
        const reviewedDate = submission.updatedAt ? formatDate(submission.updatedAt) : '';

        return (
            <div
                key={`${keyPrefix}-${submission.id}`}
                className="group rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-slate-400 hover:shadow-md"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-1 items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="flex h-20 w-16 items-center justify-center overflow-hidden rounded-md border-2 border-slate-300 bg-slate-100 text-slate-600 shadow-sm">
                                {studentImageUrl ? (
                                    <img
                                        src={getImageURL(studentImageUrl)}
                                        alt={submission.studentName}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span className="text-lg font-semibold">
                                        {submission.studentName.charAt(0)}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-base font-semibold text-slate-900">
                                    {submission.studentName}
                                </h3>
                                <span className={`rounded px-2 py-0.5 text-xs font-medium ${getSubmissionTypeColor(submission.submission_type)}`}>
                                    {submission.submission_type.toUpperCase()}
                                </span>
                                <span className="rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">
                                    {submission.submission_status}
                                </span>
                                {!isPendingView && marksBadge && (
                                    <span className="rounded px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800">
                                        {marksBadge}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-slate-600">
                                {submission.student_registration_number} • {submission.studentId}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                                Submitted on {submissionDate}
                                {!isPendingView && reviewedDate && (
                                    <>
                                        {' '}• Reviewed on {reviewedDate}
                                    </>
                                )}
                            </p>
                            <div className="mt-3 grid gap-2 md:grid-cols-2">
                                <div>
                                    <p className="text-xs font-medium text-slate-500">Subject</p>
                                    <p className="text-sm font-medium text-slate-900">
                                        {submission.subjectName} ({submission.subjectCode})
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-500">Course & Batch</p>
                                    <p className="text-sm font-medium text-slate-900">
                                        {submission.courseShortName} - {submission.batchName}
                                    </p>
                                </div>
                            </div>
                            {!isPendingView && submission.feedback && (
                                <p className="mt-3 text-sm text-slate-600">
                                    <span className="font-semibold text-slate-700">Feedback:</span> {submission.feedback}
                                </p>
                            )}
                        </div>
                    </div>

                    {isPendingView ? (
                        <div className="ml-4">
                            <button
                                onClick={() => openGradingModal(submission)}
                                className="rounded-lg bg-slate-800 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-900 hover:shadow-md"
                            >
                                Grade Now
                            </button>
                        </div>
                    ) : (
                        <div className="ml-4 flex flex-col items-end text-right text-xs text-slate-500">
                            <div className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Corrected</span>
                            </div>
                            <p className="mt-2">
                                {reviewedDate ? `Reviewed ${reviewedDate}` : 'Awaiting sync'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        fetchSubmissions(viewMode);
    }, [viewMode]);

    useEffect(() => {
        setSelectedCourse(null);
        setSelectedBatch(null);
        setSelectedSubject(null);
        setSearchTerm('');
    }, [viewMode]);

    useEffect(() => {
        if (!isPendingView && showGradingModal) {
            closeGradingModal();
        }
    }, [isPendingView, showGradingModal]);

    useEffect(() => {
        applyFilters();
    }, [submissions, selectedCourse, selectedBatch, selectedSubject, searchTerm]);

    const filterSubmissionsData = (list, courseId, batchId, subjectId, term) => {
        let filtered = [...list];

        if (courseId) {
            filtered = filtered.filter((s) => s.courseId === courseId);
        }

        if (batchId) {
            filtered = filtered.filter((s) => s.batchId === batchId);
        }

        if (subjectId) {
            filtered = filtered.filter((s) => s.subjectId === subjectId);
        }

        if (term) {
            const normalized = term.toLowerCase();
            filtered = filtered.filter(
                (s) =>
                    s.studentName.toLowerCase().includes(normalized) ||
                    s.student_registration_number.toLowerCase().includes(normalized) ||
                    s.studentId.toLowerCase().includes(normalized) ||
                    s.studentEmail?.toLowerCase().includes(normalized)
            );
        }

        return filtered;
    };

    const fetchSubmissions = async (status = viewMode) => {
        setLoading(true);
        setFilteredSubmissions([]);
        setSubmissions([]);
        setStats({ total: 0, regular: 0, topup: 0 });
        try {
            const response = await axiosInstance.get('/lecturer/pending-submissions', {
                params: { status },
            });
            const data = response?.data || {};

            if (data.success) {
                const nextSubmissions = data.submissions || [];
                const nextFilters = data.filters || { courses: [], batches: [], subjects: [] };
                const nextStats = data.stats || { total: 0, regular: 0, topup: 0 };
                const nextFiltered = filterSubmissionsData(
                    nextSubmissions,
                    selectedCourse,
                    selectedBatch,
                    selectedSubject,
                    searchTerm
                );

                setSubmissions(nextSubmissions);
                setFilters(nextFilters);
                setStats(nextStats);
                setFilteredSubmissions(nextFiltered);
            } else {
                throw new Error(data.message || data.error || 'Failed to fetch submissions');
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to fetch submissions',
            });
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (baseList = submissions) => {
        const filtered = filterSubmissionsData(
            baseList,
            selectedCourse,
            selectedBatch,
            selectedSubject,
            searchTerm
        );
        setFilteredSubmissions(filtered);
    };

    const openGradingModal = (submission) => {
        setSelectedSubmission(submission);
        setShowGradingModal(true);
    };

    const closeGradingModal = () => {
        setShowGradingModal(false);
        setSelectedSubmission(null);
    };

    const handleGradeSubmission = async (marks, comment) => {
        if (!selectedSubmission) return;

        try {
            const response = await axiosInstance.post('/lecturer/submissions/update-marks', {
                submission_id: selectedSubmission.id,
                submission_type: selectedSubmission.submission_type,
                marks,
                comment,
            });

            const data = response?.data || {};

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Assignment graded successfully',
                    timer: 2000,
                    showConfirmButton: false,
                });

                await fetchSubmissions(viewMode);
                closeGradingModal();
            } else {
                throw new Error(data.message || data.error || 'Failed to update marks');
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to update marks',
            });
        }
    };

    const getSubmissionTypeColor = (type) => {
        return type === 'regular' 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-indigo-100 text-indigo-800';
    };

    const resetFilters = () => {
        setSelectedCourse(null);
        setSelectedBatch(null);
        setSelectedSubject(null);
        setSearchTerm('');
    };

    const availableBatches = selectedCourse
        ? filters.batches.filter(b => b.courseId === selectedCourse)
        : filters.batches;

    // React-select options
    const courseOptions = filters.courses.map(c => ({
        value: c.courseId,
        label: `${c.courseShortName} - ${c.courseName}`,
    }));

    const batchOptions = availableBatches.map(b => ({
        value: b.batchId,
        label: b.batchName,
    }));

    const subjectOptions = filters.subjects.map(s => ({
        value: s.subjectId,
        label: `${s.subjectCode} - ${s.subjectName}`,
    }));

    const handleCourseChange = (option) => {
        setSelectedCourse(option ? option.value : null);
        setSelectedBatch(null);
    };

    const handleBatchChange = (option) => {
        setSelectedBatch(option ? option.value : null);
    };

    const handleSubjectChange = (option) => {
        setSelectedSubject(option ? option.value : null);
    };

    return (
        <LecturerLayout title="Correction">
            <div className="mx-auto max-w-7xl space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white/80 p-6 shadow-sm ring-1 ring-slate-100">
                    <div>
                        <p className="text-sm font-medium text-slate-500">Currently showing</p>
                        <h1 className="text-2xl font-semibold text-slate-900">{isPendingView ? 'Not Corrected Students' : 'Corrected Students'}</h1>
                        <p className="text-sm text-slate-500">{isPendingView ? 'Assignments waiting for evaluation' : 'Students you have already graded'}</p>
                    </div>
                    <div className="inline-flex overflow-hidden rounded-full border border-slate-200 bg-slate-50 p-1 shadow-inner">
                        <button
                            onClick={() => setViewMode('pending')}
                            className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                                isPendingView ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            aria-pressed={isPendingView}
                        >
                            Not Corrected
                        </button>
                        <button
                            onClick={() => setViewMode('graded')}
                            className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                                !isPendingView ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            aria-pressed={!isPendingView}
                        >
                            Corrected
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-6 md:grid-cols-3">
                    <div className="rounded-lg bg-white p-6 shadow-sm border border-slate-200 transition-all hover:shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-600">{statLabels.total}</p>
                                <p className="mt-2 text-3xl font-bold text-slate-900">{stats.total}</p>
                            </div>
                            <div className="rounded-lg bg-slate-100 p-3">
                                <svg className="h-8 w-8 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg bg-white p-6 shadow-sm border border-emerald-200 transition-all hover:shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-600">{statLabels.regular}</p>
                                <p className="mt-2 text-3xl font-bold text-emerald-700">{stats.regular}</p>
                            </div>
                            <div className="rounded-lg bg-emerald-100 p-3">
                                <svg className="h-8 w-8 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg bg-white p-6 shadow-sm border border-sky-200 transition-all hover:shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-600">{statLabels.topup}</p>
                                <p className="mt-2 text-3xl font-bold text-sky-700">{stats.topup}</p>
                            </div>
                            <div className="rounded-lg bg-sky-100 p-3">
                                <svg className="h-8 w-8 text-sky-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="rounded-lg bg-white p-6 shadow-sm border border-slate-200">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900">Filter Submissions</h2>
                        {(selectedCourse || selectedBatch || selectedSubject || searchTerm) && (
                            <button
                                onClick={resetFilters}
                                className="text-sm text-slate-600 hover:text-slate-900 font-medium"
                            >
                                Clear All Filters
                            </button>
                        )}
                    </div>

                    <div className="grid gap-4 md:grid-cols-4">
                        {/* Search */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Search Student</label>
                            <input
                                type="text"
                                placeholder="Name, Reg No, Student ID, Email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        {/* Course Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Course</label>
                            <Select
                                value={courseOptions.find(opt => opt.value === selectedCourse) || null}
                                onChange={handleCourseChange}
                                options={courseOptions}
                                isClearable
                                placeholder="All Courses"
                                className="mt-1"
                                classNamePrefix="select"
                            />
                        </div>

                        {/* Batch Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Batch</label>
                            <Select
                                value={batchOptions.find(opt => opt.value === selectedBatch) || null}
                                onChange={handleBatchChange}
                                options={batchOptions}
                                isClearable
                                placeholder="All Batches"
                                className="mt-1"
                                classNamePrefix="select"
                                isDisabled={selectedCourse === null && filters.batches.length > 10}
                            />
                        </div>

                        {/* Subject Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Subject</label>
                            <Select
                                value={subjectOptions.find(opt => opt.value === selectedSubject) || null}
                                onChange={handleSubjectChange}
                                options={subjectOptions}
                                isClearable
                                placeholder="All Subjects"
                                className="mt-1"
                                classNamePrefix="select"
                            />
                        </div>
                    </div>
                </div>

                {/* Submissions List */}
                <div className="rounded-lg bg-white shadow-sm border border-slate-200">
                    <div className="border-b border-slate-200 px-6 py-4 bg-slate-50">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-900">
                                {listTitle} ({filteredSubmissions.length})
                            </h2>
                            {filteredSubmissions.length > 0 && (
                                <p className="text-sm text-slate-600">
                                    {listSubtitle}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="relative p-6">
                        {loading ? (
                            <PageLoader
                                label="Refreshing submissions"
                                helperText="Fetching the latest assignment data, please hold on."
                            />
                        ) : filteredSubmissions.length === 0 ? (
                            <div className="py-12 text-center">
                                <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 border-2 border-emerald-200">
                                    <svg className="h-12 w-12 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900">All Caught Up!</h3>
                                <p className="mt-2 text-slate-600">
                                    {submissions.length === 0 
                                        ? (isPendingView ? 'No pending submissions at the moment.' : 'No corrected submissions yet.') 
                                        : 'No submissions match your filters.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Regular assignments section */}
                                <div>
                                    <div className="mb-3 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-slate-800">Regular Assignments</h3>
                                        <span className="text-xs text-slate-500">{filteredSubmissions.filter(s => s.submission_type === 'regular').length} {statusCountLabel}</span>
                                    </div>
                                    <div className="space-y-4">
                                        {filteredSubmissions
                                            .filter((submission) => submission.submission_type === 'regular')
                                            .map((submission) => renderSubmissionCard(submission, 'regular'))}
                                    </div>
                                </div>

                                {/* Top-up assignments section */}
                                <div>
                                    <div className="mb-3 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-slate-800">Top-Up Assignments</h3>
                                        <span className="text-xs text-slate-500">{filteredSubmissions.filter(s => s.submission_type === 'topup').length} {statusCountLabel}</span>
                                    </div>
                                    <div className="space-y-4">
                                        {filteredSubmissions
                                            .filter((submission) => submission.submission_type === 'topup')
                                            .map((submission) => renderSubmissionCard(submission, 'topup'))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Grading Modal */}
            {showGradingModal && selectedSubmission && (
                <GradingModal
                    submission={selectedSubmission}
                    studentImageUrl={selectedSubmission.studentImage}
                    onClose={closeGradingModal}
                    onSubmit={handleGradeSubmission}
                />
            )}
        </LecturerLayout>
    );
}

// Grading Modal Component
function GradingModal({ submission, studentImageUrl, onClose, onSubmit }) {
    const [selectedGrade, setSelectedGrade] = useState('');
    const [numericMarks, setNumericMarks] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const grades = [
        { value: 'D', label: 'Distinction', color: 'from-green-500 to-emerald-600', description: 'Outstanding work!' },
        { value: 'M', label: 'Merit', color: 'from-blue-500 to-cyan-600', description: 'Very good performance' },
        { value: 'P', label: 'Pass', color: 'from-yellow-500 to-orange-500', description: 'Satisfactory work' },
        { value: 'R', label: 'Refer', color: 'from-red-500 to-pink-600', description: 'Needs improvement' },
    ];

    const isTopup = submission.submission_type === 'topup';

    const handleSubmit = async () => {
        const marksValue = isTopup ? numericMarks : selectedGrade;
        
        if (!marksValue) {
            Swal.fire({
                icon: 'warning',
                title: isTopup ? 'Marks Required' : 'Grade Required',
                text: isTopup ? 'Please enter marks before submitting' : 'Please select a grade before submitting',
            });
            return;
        }

        if (isTopup) {
            const marks = parseFloat(numericMarks);
            if (isNaN(marks) || marks < 0 || marks > 100) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid Marks',
                    text: 'Please enter a valid number between 0 and 100',
                });
                return;
            }
        }

        setSubmitting(true);
        await onSubmit(marksValue, comment);
        setSubmitting(false);
    };

    const getMarksColor = (marks) => {
        if (marks >= 70) return 'text-green-600';
        if (marks >= 60) return 'text-blue-600';
        if (marks >= 40) return 'text-yellow-600';
        return 'text-red-600';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold">Grade Assignment</h2>
                            <p className="mt-1 text-slate-200">
                                {isTopup ? 'Enter numeric marks (0-100) 📊' : 'Make a positive impact! 🎯'}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-full p-2 text-white hover:bg-white hover:bg-opacity-20"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Student Info Card */}
                    <div className="mb-6 rounded-lg bg-slate-50 p-4">
                        <div className="flex items-start gap-4">
                            <div className="flex h-24 w-20 items-center justify-center overflow-hidden rounded-md border-2 border-slate-300 bg-slate-100 text-slate-600 shadow-sm">
                                {studentImageUrl ? (
                                    <img
                                        src={getImageURL(studentImageUrl)}
                                        alt={submission.studentName}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span className="text-2xl font-semibold">
                                        {submission.studentName.charAt(0)}
                                    </span>
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-slate-900">{submission.studentName}</h3>
                                <p className="text-sm text-slate-600">{submission.student_registration_number} • {submission.studentId}</p>
                                <div className="mt-2 grid gap-1 text-sm">
                                    <p><span className="font-medium">Subject:</span> {submission.subjectName}</p>
                                    <p><span className="font-medium">Batch:</span> {submission.batchName}</p>
                                    <p>
                                        <span className="font-medium">Type:</span>{' '}
                                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                                            isTopup ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                            {submission.submission_type.toUpperCase()}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Grade/Marks Selection */}
                    {isTopup ? (
                        <div className="mb-6">
                            <label className="mb-3 block text-sm font-semibold text-slate-900">
                                Enter Marks (0-100) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    value={numericMarks}
                                    onChange={(e) => setNumericMarks(e.target.value)}
                                    placeholder="Enter marks (e.g., 75.5)"
                                    className="w-full rounded-lg border-2 border-slate-300 px-4 py-3 text-lg font-semibold focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500"
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {numericMarks && !isNaN(parseFloat(numericMarks)) && (
                                        <span className={`text-2xl font-bold ${getMarksColor(parseFloat(numericMarks))}`}>
                                            {parseFloat(numericMarks).toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                            {numericMarks && !isNaN(parseFloat(numericMarks)) && (
                                <div className="mt-2">
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                        <div
                                            className={`h-full transition-all ${
                                                parseFloat(numericMarks) >= 70 ? 'bg-green-500' :
                                                parseFloat(numericMarks) >= 60 ? 'bg-blue-500' :
                                                parseFloat(numericMarks) >= 40 ? 'bg-yellow-500' :
                                                'bg-red-500'
                                            }`}
                                            style={{ width: `${Math.min(parseFloat(numericMarks), 100)}%` }}
                                        ></div>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-600">
                                        {parseFloat(numericMarks) >= 70 ? '🌟 Excellent!' :
                                         parseFloat(numericMarks) >= 60 ? '👍 Good!' :
                                         parseFloat(numericMarks) >= 40 ? '✓ Pass' :
                                         '⚠️ Below Pass'}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="mb-6">
                            <label className="mb-3 block text-sm font-semibold text-slate-900">
                                Select Grade <span className="text-red-500">*</span>
                            </label>
                            <div className="grid gap-3 md:grid-cols-2">
                                {grades.map((grade) => (
                                    <button
                                        key={grade.value}
                                        onClick={() => setSelectedGrade(grade.value)}
                                        className={`rounded-lg border-2 p-4 text-left transition-all ${
                                            selectedGrade === grade.value
                                                ? 'border-transparent bg-gradient-to-r ' + grade.color + ' text-white shadow-lg'
                                                : 'border-slate-300 bg-white hover:border-slate-400'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-lg font-bold">{grade.label}</div>
                                                <div className={`text-sm ${selectedGrade === grade.value ? 'text-white' : 'text-slate-600'}`}>
                                                    {grade.description}
                                                </div>
                                            </div>
                                            <div className={`text-2xl font-bold ${selectedGrade === grade.value ? 'text-white' : 'text-slate-400'}`}>
                                                {grade.value}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Comment */}
                    <div className="mb-6">
                        <label className="mb-2 block text-sm font-semibold text-slate-900">
                            Feedback (Optional)
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Add constructive feedback to help the student improve..."
                            rows={4}
                            className="w-full rounded-lg border border-slate-300 p-3 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500"
                            maxLength={1000}
                        />
                        <p className="mt-1 text-xs text-gray-500">{comment.length}/1000 characters</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 rounded-lg border-2 border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || (!isTopup && !selectedGrade) || (isTopup && !numericMarks)}
                            className="flex-1 rounded-lg bg-slate-800 px-6 py-3 font-semibold text-white shadow-md transition-all hover:bg-slate-900 hover:shadow-lg disabled:opacity-50"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                    Submitting...
                                </span>
                            ) : (
                                'Submit Grade'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
